import { oc } from "@orpc/contract";
import { z } from "zod";
import { SpaQuoteType } from "@erp/shared-types";

export const SpaLineItemResponseSchema = z.object({
  id: z.uuid(),
  spaId: z.uuid(),
  productNumber: z.string(),
  rowIndex: z.number().int(),
  rawData: z.record(z.string(), z.unknown()),
  keptFlag: z.boolean(),
  dropReason: z
    .enum([
      "blank_row",
      "missing_catalog",
      "deleted",
      "expired",
      "future_start",
      "ecom_only",
    ])
    .nullable(),
  currentMargin: z.number().nullable(),
  requestedMargin: z.number().nullable(),
  approvalDecision: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const SpaLinkedRequestResponseSchema = z.object({
  id: z.uuid(),
  requestNumber: z.string(),
  status: z.string(),
  createdAt: z.coerce.date(),
});

export const SpaResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  spaRequestId: z.uuid().nullable(),
  sourceDocumentId: z.uuid(),
  vendor: z.string(),
  spaNumber: z.string(),
  quoteType: z.nativeEnum(SpaQuoteType).nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  currency: z.string().nullable(),
  customerNumber: z.string().nullable(),
  division: z.string().nullable(),
  request: SpaLinkedRequestResponseSchema.nullable(),
  lineItems: z.array(SpaLineItemResponseSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const SpaListItemSchema = SpaResponseSchema.omit({
  lineItems: true,
});

export const ListSpasInputSchema = z.object({
  search: z.string().optional(),
  /** Substring match against SpaLineItem.productNumber. */
  productNumber: z.string().optional(),
  /** Agreement validity window overlaps [validFrom, validTo] (both optional, either bound open-ended). */
  validFrom: z.string().optional(),
  validTo: z.string().optional(),
  limit: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
});

export const GetSpaInputSchema = z.object({
  id: z.uuid(),
});

export const DeleteSpaInputSchema = z.object({
  id: z.uuid(),
});

export const spasContracts = {
  list: oc
    .input(ListSpasInputSchema)
    .output(
      z.object({
        items: z.array(SpaListItemSchema),
        total: z.number().int(),
      }),
    )
    .route({ method: "GET" }),

  get: oc.input(GetSpaInputSchema).output(SpaResponseSchema).route({
    method: "GET",
  }),

  /**
   * Soft-delete a persisted SPA record and its line items. The source request remains.
   */
  delete: oc
    .input(DeleteSpaInputSchema)
    .output(
      z.object({
        id: z.uuid(),
        spaRequestId: z.uuid().nullable(),
        deleted: z.literal(true),
      }),
    )
    .route({ method: "DELETE" }),
} as const;

export type SpaResponse = z.infer<typeof SpaResponseSchema>;
export type SpaListItem = z.infer<typeof SpaListItemSchema>;
export type SpaLineItemResponse = z.infer<typeof SpaLineItemResponseSchema>;
